<template>
    <template v-for="entry in navigation">
        <div
            v-if="shouldRenderEntry(entry)"
            v-show="showNavigation(entry)"
            id="menu"
            ref="menuElement"
            :key="entry.id"
            class="pt-16 hidden md:flex flex-col"
        >
            <nav
                v-if="onlySubcategories && getActiveChild(entry)"
                class="mt-2"
                :aria-labelledby="`navigation-sidebar-subtree-${entry.id}`"
            >
                <MiscLink
                    no-prefetch
                    :to="formatLink(getCategoryRoute(getActiveChild(entry)!))"
                    class="block py-2 sidebar-navigation-link text-sm"
                >
                    <span :id="`navigation-sidebar-subtree-${entry.id}`" class="block font-semibold leading-6 text-primary brand-page-color-active:text-brand-page-color">{{ getTranslatedProperty(getActiveChild(entry)!, 'name') }}</span>
                </MiscLink>

                <LayoutNavigationSidebarSubcategory :child="getActiveChild(entry)!" />
            </nav>

            <template v-else>
                <nav
                    v-for="(group, groupType) in groupCategoriesBySection(entry.children)"
                    :key="groupType"
                    class="mb-8 first:mt-2 last:mb-0"
                    :class="[(isChangeOrder && groupType === 'is_highlight') ? 'order-1' : 'order-2']"
                    :aria-labelledby="`navigation-sidebar-${groupType}`"
                >
                    <template v-if="group.children.length > 0">
                        <div :id="`navigation-sidebar-${groupType}`" class="mb-3 pb-2 font-semibold text-sm border-b leading-4">
                            {{ group.name }}
                        </div>

                        <ul>
                            <li v-for="childEntry in group.children" :key="childEntry.id">
                                <MiscLink
                                    no-prefetch
                                    :to="formatLink(getCategoryRoute(childEntry))"
                                    class="block py-2 sidebar-navigation-link text-sm"
                                >
                                    <span class="block" :class="selectedNavigationItems[childEntry.id] ? 'text-primary font-semibold' : 'text-base-content-subtle hover:underline hover:text-primary brand-page-color-active:text-brand-page-color'">{{ getTranslatedProperty(childEntry, 'name') }}</span>
                                </MiscLink>

                                <LayoutNavigationSidebarSubcategory v-if="selectedNavigationItems[childEntry.id]" :child="childEntry" :index="groupType+1" />
                            </li>
                        </ul>
                    </template>
                </nav>
            </template>
        </div>
    </template>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { useWindowSize } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { getCategoryRoute, getTranslatedProperty } from '@shopware/helpers'

const props = withDefaults(defineProps<{
    navigation: Schemas["NavigationRouteResponse"]
    /** Only render the active child of the top-level entry and its subtree (CMS config `onlySubcategories`) */
    onlySubcategories?: boolean
}>(), {
    onlySubcategories: false,
})
const route = useRoute()
const { t } = useI18n()

// Computed so the selection follows the deep tree once it replaces the shallow SSR tree
const selectedNavigationItems = computed(() => {
    const selected: Record<string, boolean> = {}

    props.navigation?.forEach(item => {
        selected[item.id] = true

        item?.children?.forEach(child => {
            if (isCategoryRouteActive(route.path, child)) {
                selected[child.id] = true
            }
        })
    })

    return selected
})

function getActiveChild(entry: Schemas['Category']) {
    return entry.children?.find(child => selectedNavigationItems.value[child.id])
}

function shouldRenderEntry(entry: Schemas['Category']) {
    if (!entry.children?.length) return false

    // Subtree mode on a category without subcategories renders nothing; the top-level page itself
    // (no active child) falls back to the full grouped navigation
    const activeChild = getActiveChild(entry)
    return !(props.onlySubcategories && activeChild && !activeChild.children?.length)
}

const prefix = inject('urlPrefix')
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)
const pathSegments = route.path.split('/')
const topCategory = prefix !== '' ? pathSegments[2] : pathSegments[1]
const menuElement = ref<HTMLElement[]>([])
const activeMenuElement = ref<undefined | HTMLElement>(undefined)
const headerElement = ref<null | HTMLElement>(null)
const footerElement = ref<null | HTMLElement>(null)
const lastScrollTop = ref(0)
const isChangeOrder = ref(false)

function isInCurrentHighlightCategory(categories: Schemas["Category"][]) {
    /*
    ** loop through all categories in is_highlight to change order in sidenav
    ** highlight categories block should be shown first
    ** if highlight category is active
    */
    categories.forEach((category) => {
        const localisedCategoryUrl = formatLink(getCategoryRoute(category))
        if (route.path === localisedCategoryUrl) {
            isChangeOrder.value = true
            return
        }

        if (category.children) {
            category.children.forEach((cat) => {
                const localisedCategoryChildUrl = formatLink(getCategoryRoute(cat))
                if (route.path === localisedCategoryChildUrl) {
                    isChangeOrder.value = true
                    return
                }
            })
        }
    })
}

function showNavigation (category: Schemas['Category']) {
    const categoryRoute = getCategoryRoute(category)
    const categoryPath = typeof categoryRoute === 'string' ? categoryRoute : categoryRoute.path
    const clearedCategoryPath = categoryPath.startsWith('/') ? categoryPath.substring(1) : categoryPath
    return clearedCategoryPath === topCategory
}

const { height: windowHeight } = useWindowSize()
const isWindowHigh = computed(() => {
    return windowHeight.value > 900
})
function handleNavigationScrollBehaviour() {
    if (activeMenuElement.value?.offsetHeight && footerElement.value?.offsetHeight) {
        const isScrollAboveFooter = footerElement.value.getBoundingClientRect().top > window.innerHeight
        const isScrollPositionTop = activeMenuElement.value.getBoundingClientRect().top <= activeMenuElement.value.offsetTop
        const currentScrollTop = window.scrollY
        if (isScrollPositionTop && isScrollAboveFooter) {
            let deltaScroll = lastScrollTop.value - currentScrollTop
            if (deltaScroll > 0 && isWindowHigh.value) {
                // slow down upward scroll movement on high windows
                // scrollTop must reach 0
                deltaScroll -= .7
            }
            activeMenuElement.value.scrollTop -= deltaScroll
        }
        lastScrollTop.value = currentScrollTop
    }
}

onMounted(() => {
    if (menuElement.value != null) {
        activeMenuElement.value = menuElement.value?.find((element) => element.style.display !== 'none')
        if (activeMenuElement.value?.offsetHeight && activeMenuElement.value?.parentElement?.offsetHeight) {
            headerElement.value = document.querySelector('header')
            footerElement.value = document.querySelector('footer')
            if (headerElement.value?.offsetHeight) {
                if ((activeMenuElement.value?.parentElement?.offsetHeight + headerElement.value?.clientHeight) <= window.innerHeight) {
                    activeMenuElement.value.parentElement.style.position = 'sticky'
                    activeMenuElement.value.parentElement.style.top = '0'
                    activeMenuElement.value.classList.remove('pt-16') // prevent layout shift
                    activeMenuElement.value.parentElement.parentElement?.classList.add('pt-20')
                } else {
                    activeMenuElement.value.parentElement.style.position = 'sticky'
                    activeMenuElement.value.parentElement.style.top = '0'
                    activeMenuElement.value.classList.add('overflow-hidden', 'h-[100vh]')
                    activeMenuElement.value.classList.remove('pt-16')
                    activeMenuElement.value.parentElement.parentElement?.classList.add('pt-20')
                    window.addEventListener('scroll', handleNavigationScrollBehaviour)
                }
            }
        }
    }
})

onUnmounted(() => {
    window.removeEventListener('scroll', handleNavigationScrollBehaviour)
})

function groupCategoriesBySection(categories: Schemas["Category"][]): Record<string, { name: string; children: Schemas["Category"][] }> {
    const result: Record<string, { name: string; children: Schemas["Category"][] }> = {
        categories: {
            name: t('layout.navigation.sidebar.categoriesTitle'),
            children: [],
        },
        is_highlight: {
            name: t('layout.navigation.sidebar.highlightsTitle'),
            children: [],
        },
        is_new_sale: {
            name: t('layout.navigation.sidebar.newSaleTitle'),
            children: [],
        },
        is_top_brand: {
            name: t('layout.navigation.sidebar.topBrandTitle'),
            children: [],
        },
    }

    categories.forEach((item: Schemas["Category"]) => {
        const section = item.customFields?.custom_category_flyout_section || 'categories'
        result?.[section]?.children.push(item)
    })

    if (result?.['is_highlight']?.children) {
        isInCurrentHighlightCategory(result['is_highlight'].children)
    }

    // reorder the array is_highlight in itself to have active category on top
    if (isChangeOrder.value) {
        result?.['is_highlight']?.children?.forEach((cat) => {
            const localisedCategoryUrl = formatLink(getCategoryRoute(cat))
            if (route.path === localisedCategoryUrl && result?.['is_highlight']?.children) {
                result['is_highlight'].children = [...result['is_highlight'].children].sort((a, b) => {
                    if (a.name === cat.name) return -1
                    if (b.name === cat.name) return 1
                    return 0
                })
            }

            if (cat.children) {
                cat.children.forEach((catChild) => {
                    const localisedCategoryChildUrl = formatLink(getCategoryRoute(catChild))
                    if (route.path === localisedCategoryChildUrl && result?.['is_highlight']?.children) {
                        result['is_highlight'].children = [...result['is_highlight'].children].sort((a, b) => {
                            if (a.name === cat.name) return -1
                            if (b.name === cat.name) return 1
                            return 0
                        })
                    }
                })
            }
        })
    }

    return result
}
</script>
